import { formatMonthLabelKey, getBillableMode } from './dashboardMath'
import { constants } from '../config'

const { graphFilters } = constants

/**
 * Flat ticket/vuln/upgrade rows for the data drawer visualizer.
 */
const collectTicketsForSupportType = ({
  graphData,
  activeTab,
  projectData,
  filtersSelected,
  billableSelected,
  listBillableMode,
  supportTypeFilter
}) => {
  const tabGraph = graphData?.[activeTab]
  if (!tabGraph) return []

  const labels = Array.isArray(tabGraph.labels) ? tabGraph.labels : []
  const datasets = Array.isArray(tabGraph.datasets) ? tabGraph.datasets : []

  if (!labels.length || !datasets.length) return []

  const selectedProjects = datasets
    .map((d) => d?.label)
    .filter((name) => name && name !== 'Average' && name !== 'Max' && projectData?.[name])

  if (selectedProjects.length === 0) return []

  const billableMode = listBillableMode ?? getBillableMode(billableSelected || {})
  const issueTypeActiveInChart = Boolean(filtersSelected?.[supportTypeFilter])

  const allTickets = []

  labels.forEach((label) => {
    const monthKey = formatMonthLabelKey(label)

    selectedProjects.forEach((projectName) => {
      const monthData = projectData?.[projectName]?.[monthKey]
      if (!monthData || typeof monthData !== 'object') return

      const baseItems = (() => {
        if (supportTypeFilter === graphFilters.SUPPORT) {
          return Array.isArray(monthData.Tickets)
            ? monthData.Tickets.map((t) => ({ ...t, supportType: graphFilters.SUPPORT }))
            : []
        }
        if (supportTypeFilter === graphFilters.VULNERABILITIES) {
          return Array.isArray(monthData.Vulnerabilities)
            ? monthData.Vulnerabilities.map((t) => ({ ...t, supportType: graphFilters.VULNERABILITIES }))
            : []
        }
        if (supportTypeFilter === graphFilters.UPGRADES) {
          return Array.isArray(monthData.Upgrades)
            ? monthData.Upgrades.map((t) => ({ ...t, supportType: graphFilters.UPGRADES }))
            : []
        }
        return []
      })()

      const billableFiltered = baseItems.filter((t) => {
        if (!t || typeof t !== 'object') return false
        if (billableMode === 'all') return true
        if (billableMode === 'billable') return t.isBillable === true
        if (billableMode === 'nonbillable') return t.isBillable !== true
        return true
      })

      billableFiltered.forEach((ticket) => {
        allTickets.push({
          ...ticket,
          _project: projectName,
          _monthLabel: label,
          _monthKey: monthKey,
          _issueTypeActiveInChart: issueTypeActiveInChart
        })
      })
    })
  })

  return allTickets
}

/**
 * Same grouping as TicketVisualizer (one card per Zendesk id / Jira key per project).
 */
const groupTicketsForDrawer = (tickets) => {
  const byZendesk = new Map()
  let noidCounter = 0

  tickets.forEach((entry) => {
    const zendeskID = entry?.zendeskID != null ? String(entry.zendeskID) : null
    const project = entry?._project || ''
    const key = zendeskID
      ? `${zendeskID}|${project}`
      : `${entry?.jiraIssueKey ?? entry?.key ?? entry?.ticketKey ?? entry?.id ?? `noid-${++noidCounter}`}|${project}`

    if (!byZendesk.has(key)) {
      byZendesk.set(key, {
        ticket: { ...entry },
        appearances: [],
        jiraAttached: []
      })
    }

    const group = byZendesk.get(key)
    group.ticket._issueTypeActiveInChart = Boolean(group.ticket._issueTypeActiveInChart || entry._issueTypeActiveInChart)
    if (entry?.comments != null && String(entry.comments).trim()) {
      group.ticket.comments = entry.comments
    }
    group.appearances.push({
      project: entry._project,
      monthLabel: entry._monthLabel,
      monthKey: entry._monthKey,
      revisedSP: Number(entry?.revisedSP || 0) || 0
    })

    if (entry?.jiraIssueKey) {
      const revisedSP = Number(entry?.revisedSP || 0) || 0
      const storyPointsEstimated =
        entry?.storyPointsEstimated != null && String(entry.storyPointsEstimated) !== 'null'
          ? Number(entry.storyPointsEstimated)
          : null
      const existing = group.jiraAttached.find((j) => (j.key || j.jiraIssueKey) === entry.jiraIssueKey)
      if (!existing) {
        group.jiraAttached.push({
          key: entry.jiraIssueKey,
          jiraIssueKey: entry.jiraIssueKey,
          summary: entry.jiraSummary ?? entry.jiraSubject ?? '',
          title: entry.jiraSummary ?? entry.jiraSubject ?? '',
          description: entry.jiraDescription ?? '',
          revisedSP,
          storyPointsEstimated
        })
      } else {
        existing.revisedSP = (Number(existing.revisedSP) || 0) + revisedSP
      }
    }
  })

  return Array.from(byZendesk.values()).map(({ ticket, appearances, jiraAttached }) => ({
    ...ticket,
    _appearances: appearances,
    _jiraAttached: jiraAttached
  }))
}

const getGroupedCountForSupportType = (viewProps, supportTypeFilter, listBillableMode = 'all') => {
  const tickets = collectTicketsForSupportType({ ...viewProps, supportTypeFilter, listBillableMode })
  return groupTicketsForDrawer(tickets).length
}

/**
 * Grouped card counts per support type for drawer tab badges (always full set — not graph billable mode).
 */
const getDrawerTicketCountsBySupportType = (viewProps) => ({
  [graphFilters.SUPPORT]: getGroupedCountForSupportType(viewProps, graphFilters.SUPPORT, 'all'),
  [graphFilters.VULNERABILITIES]: getGroupedCountForSupportType(viewProps, graphFilters.VULNERABILITIES, 'all'),
  [graphFilters.UPGRADES]: getGroupedCountForSupportType(viewProps, graphFilters.UPGRADES, 'all')
})

export {
  collectTicketsForSupportType,
  groupTicketsForDrawer,
  getDrawerTicketCountsBySupportType,
  getGroupedCountForSupportType
}
